import type { FC, ReactNode } from 'react'
import { useEffect } from 'react'

import { AuthClient, decodeToken, getValidPhoneNumberFromUrl } from '@skytech/auth'
import { config } from '@skytech/config'
import { AppWithMonitoring } from '@skytech/monitoring'
import { CookieConsent, PageLoader } from '@skytech/ui'
import {
  checkLocaleAgainstTranslations,
  DEFAULT_LOCALE,
  getBrowserLocale,
  getDefaultCountryId,
  getWhiteLabel,
  getWhiteLabelBasedOnId,
} from '@skytech/utils'
import { QueryClientProvider, useQuery } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { polyfillCountryFlagEmojis } from 'country-flag-emoji-polyfill'
import { IntlProvider } from 'react-intl'
import { Provider as ReduxProvider, useDispatch } from 'react-redux'
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { createStore, applyMiddleware, compose } from 'redux'
import { thunk } from 'redux-thunk'

import queries from '@/api/queries'
import statusResources from '@/api/status'
import translationResources from '@/api/translations'
import userResources from '@/api/user'
import { AppContextProvider, useAppContext } from '@/App.context'
import Login from '@/components/login/Login'
import Logout from '@/components/Logout'
import { setupMessages } from '@/components/messages/messagesActions'
import useLogIn from '@/hooks/useLogin'
import { APP_ROUTES } from '@/util/appRoutes'

import { consentResources } from './api/consent'
import { Components } from './components/Components'
import { useTranslationWithJSXSupport } from './components/i18n/hooks'
import { Banner } from './components/layout/Banner'
import MetaTags from './components/layout/MetaTags'
import { Navigation } from './components/layout/Navigation'
import Messages from './components/messages/Messages'
import Modal from './components/modal/Modal'
import { CartProvider } from './contexts/cart/cart-context'
import reducer from './ducks'
import { EmployeeConsentPopup } from './EmployeeConsentPopup'
import { queryClient } from './queryClient'
import type { TokenData } from './types'
import getUserFromToken from './util/getUserFromToken'

import './style/body.scss'
import './style/animations.scss'

const AuthenticatedPages: FC = () => {
  const context = useAppContext()
  const dispatch = useDispatch()

  const { data: translationList } = useQuery(
    queries.translationList(),
    translationResources.getList,
    { staleTime: Infinity }
  )

  const { setLocale, setWhiteLabel, setIsMobileNavBar } = useAppContext()

  const { data: user, isLoading: isUserLoading } = useQuery(queries.user(), userResources.get, {
    retry: 0,
    staleTime: Infinity,
    onError() {
      AuthClient.logOut({ isTriggeredProgrammatically: true })
    },
  })

  const { data: activeConsent, isLoading: isActiveConsentLoading } = useQuery(
    queries.activeConsent(),
    consentResources.getActiveConsents,
    {
      enabled: Boolean(user),
      onSuccess(data) {
        if (!data?.id) return
        context.setIsActiveEmployeeConsentModalOpen(!data.isConsentGiven)
      },
    }
  )

  const { data: status, isLoading: isStatusLoading } = useQuery(
    queries.status(),
    statusResources.get,
    {
      enabled: Boolean(user),
      staleTime: Infinity,
      onSuccess(status) {
        const objectLocale = status.object.locale
        const customerLocale = status.setup?.locale

        if (!translationList) return

        const locale = objectLocale || customerLocale || getBrowserLocale()

        const updatedLocale = checkLocaleAgainstTranslations({
          locale,
          translationList,
          defaultLocale: DEFAULT_LOCALE,
        })

        // Open navbar in case of no traffic data for mobile users on the root route
        if (location.pathname === '/' && status.invoices.length === 0) {
          setIsMobileNavBar(true)
        }

        setLocale(updatedLocale)

        const whiteLabel = getWhiteLabel(status.setup.white_label)
        setWhiteLabel(whiteLabel)

        dispatch(
          // @ts-ignore
          setupMessages({
            setup: status.setup,
            user: status.object,
            locale: updatedLocale,
          })
        )
      },
    }
  )

  if (isUserLoading || !user || isStatusLoading || !status?.object || isActiveConsentLoading)
    return <PageLoader />

  // let isDevelopmentMode = config.ENVIRONMENT === 'dev'
  // const { whiteLabel } = useAppContext()
  // if (!isDevelopmentMode)
  //   return (
  //     <div className={`login-form-wrapper ${whiteLabel}`}>
  //       <div className="maintenance-mode-message">
  //         <InfoBox emphasis="warning">
  //           <Translation id="The application is currently undergoing maintenance. Please try again later." />
  //         </InfoBox>
  //       </div>
  //     </div>
  //   )

  return (
    <div className="flex h-screen flex-col">
      <CartProvider>
        <Banner />
        <div className="relative flex flex-1 overflow-hidden">
          <Navigation />
          <Components />
        </div>
        <Messages />
        <Modal />
        {activeConsent && <EmployeeConsentPopup activeConsent={activeConsent} />}
      </CartProvider>
    </div>
  )
}

const AppCore: FC = () => {
  polyfillCountryFlagEmojis()

  const translateWithJSXSupport = useTranslationWithJSXSupport()
  const location = useLocation()
  const navigate = useNavigate()
  const { locale, setWhiteLabel } = useAppContext()

  const loginMutation = useLogIn({ isUrlCredentials: true })

  const searchParams = new URLSearchParams(location.search)
  const urlPassword = searchParams.get('key')
  const urlIdentifier = getValidPhoneNumberFromUrl(location.pathname)
  const urlToken = searchParams.get('token')

  const isUrlCredentials = urlIdentifier && urlPassword
  useEffect(() => {
    if (urlToken) {
      AuthClient.setToken(urlToken)

      const tokenData: TokenData = decodeToken(urlToken)
      const user = getUserFromToken(tokenData)

      queryClient.setQueryData(queries.user(), user)
      // Needs to be set manually as the hook has not been fired yet
      setWhiteLabel(getWhiteLabelBasedOnId(user.whiteLabelId))

      const url = new URL(window.location.href)
      url.searchParams.delete('token')
      navigate(url)
    }
  }, [navigate, setWhiteLabel, urlToken])

  useEffect(() => {
    if (isUrlCredentials) {
      const defaultPhoneCountryId = getDefaultCountryId(locale)
      loginMutation.mutate({
        phoneNumberOrEmail: urlIdentifier,
        accessCode: urlPassword,
        phoneCountryId: defaultPhoneCountryId,
      })
    }
    // todo: debug why the mutation isn't memoized when we've bumped the react-query
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlIdentifier, urlPassword])

  if (loginMutation.isLoading || isUrlCredentials || urlToken) return <PageLoader />

  const isDevEnv = config.ENVIRONMENT === 'dev'

  return (
    <>
      <Routes>
        <Route path={APP_ROUTES.login} element={<Login />} />
        <Route path={APP_ROUTES.logout} element={<Logout />} />
        <Route path="*" element={<AuthenticatedPages />} />
      </Routes>

      {!isDevEnv && (
        <CookieConsent
          onRejectCookies={() => {
            if (!AuthClient.getToken()) return
            AuthClient.logOut({ isTriggeredProgrammatically: true })
          }}
          onTranslate={translateWithJSXSupport}
        />
      )}
    </>
  )
}

const AppIntl: FC<{ children: ReactNode }> = ({ children }) => {
  const { locale } = useAppContext()

  const { data: translationList, isLoading: isTranslationsLoading } = useQuery(
    queries.translationList(),
    translationResources.getList,
    { staleTime: Infinity }
  )

  if (isTranslationsLoading) return <PageLoader />

  // todo: add the missing error page - use no.json etc.
  if (!translationList) throw new Error('Kunne ikke laste oversettelser.')

  return (
    <IntlProvider
      locale={locale}
      defaultLocale={DEFAULT_LOCALE}
      messages={translationList[locale]}
      // Do not show missing translations errors
      onError={() => {}}
    >
      {children}
    </IntlProvider>
  )
}

const composeEnhancers = (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose
const reduxStore = createStore(reducer, composeEnhancers(applyMiddleware(thunk)))

export const App: FC = () => (
  <AppWithMonitoring>
    <ReduxProvider store={reduxStore}>
      <Router>
        <QueryClientProvider client={queryClient}>
          <AppContextProvider>
            <AppIntl>
              <AppCore />

              <MetaTags />
            </AppIntl>
          </AppContextProvider>

          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </Router>
    </ReduxProvider>
  </AppWithMonitoring>
)
